import PatientTable from "./PatientTable";
import {
  Box,
  Button,
  FormControl,
  Grid,
  IconButton,
  MenuItem,
  Select,
  SelectChangeEvent,
  Typography,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import SearchForm from "./DashboardSearchForm";
import SearchIcon from '@mui/icons-material/Search';
import AddIcon from '@mui/icons-material/Add';
import { useEffect, useState } from "react";
import { AppointmentServiceInternal } from "../../services/appointment.service";
import PaginationControl from "../Layout/Pagination";
import "../Layout/Pagination/index.css";
import FullPageLoader from "../Shared/FullPageLoader";
import useStore from "../../store/store";
import { RECORDS_PER_PAGE } from '../../constants/index';
import { debounce } from 'lodash';
import { AppointmentSearchRequest } from "../../types";

const recordsPerPage = RECORDS_PER_PAGE;

interface Facility {
  id: number;
  officer_name: string;
  name: string;
  timeZone: string;
}

function FacilityDashboard() {
  const user: any = useStore();
  const [data, setData] = useState([]);
  const [currentFacility, setCurrentFacility] = useState<Facility | null>(null);
  const [refreshForFacilityUpdate, setRefreshForFacilityUpdate] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [userFacilities, setUserFacilities] = useState<Facility[] | null>(null);
  const [search, setSearch] = useState("");
  const [startDate, setStartDate] = useState<Date | null>(null);
  const [endDate, setEndDate] = useState<Date | null>(null);
  const [startIndex, setStartIndex] = useState(0);
  const [endIndex, setEndIndex] = useState(0);
  const [refresh, setRefresh] = useState<boolean>(false);
  const paginatedData = data?.slice(startIndex, endIndex);

  const handlePageChange = (pageNumber: number) => {
    setCurrentPage(pageNumber);
  };

  const handleSearchChange = (search: string) => {
    setSearch(search);
  };
  const debouncedSearchChange = debounce(handleSearchChange, 500);

  const handleSearchChangeDebounced = (search: string) => {
    debouncedSearchChange(search);
  };

  const handleStartDateChange = (date: string) => {
    const formateDate = new Date(date);
    setStartDate(formateDate);
  };

  const handleEndDateChange = (dateString: string) => {
    const date = new Date(dateString);
    setEndDate(date);
  };

  const handleFacilityChange = (event: SelectChangeEvent) => {
    let selectedFacilityId = parseInt(event.target.value, 10);
    let selectedFacility = userFacilities?.find(f => f.id === selectedFacilityId);
    setCurrentFacility(selectedFacility ?? null);
    setRefreshForFacilityUpdate(true);
  }

  const fetchDataFromApi = async () => {
    try {
      setIsLoading(true);
      const today = new Date();
      const nextWeek = new Date(today);
      nextWeek.setDate(nextWeek.getDate() + 7);

      let payload: AppointmentSearchRequest = {
        startDate: startDate?.toISOString() ?? today.toISOString(),
        endDate: endDate?.toISOString() ?? nextWeek.toISOString(),
        userDob: null,
        searchQuery: search,
        stateSearchQuery: "",
        correctionalFacilityId: currentFacility?.id ?? user.getUser().id_correctional
      }

      const response = await AppointmentServiceInternal.getappointment(payload);

      if (response?.data.isSuccess) {
        setData(response.data.data);
        setIsLoading(false);
      }
    } catch (error) {
      setIsLoading(false);
      console.error('Error fetching data from API:', error);
    }
  }

  const navigate = useNavigate();
  const handleClick = (e: any) => {
    navigate("/new-appointment");
  };

  const getFullName = () => {
    const firstName = user.getUser() && user.getUser().first_name ? user.getUser().first_name + " " + user.getUser().last_name : '';
    return `${firstName}`;
  };

  useEffect(() => {
    const newStartIndex = (currentPage - 1) * recordsPerPage;
    const newEndIndex = newStartIndex + recordsPerPage;
    setStartIndex(newStartIndex);
    setEndIndex(Math.min(newEndIndex, data.length));
  }, [currentPage, data]);

  useEffect(() => {
    fetchDataFromApi();
    setCurrentPage(1);
  }, [search, startDate, endDate]);

  useEffect(() => {
    if (refresh) {
      fetchDataFromApi();
      setCurrentPage(1);
      setRefresh(false);
    }
  }, [refresh]);

  useEffect(() => {
    if (refreshForFacilityUpdate && currentFacility) {
      setRefresh(true);
      setRefreshForFacilityUpdate(false);
    }
  }, [currentFacility, refreshForFacilityUpdate]);

  useEffect(() => {
    if (!user) return;
    const defaultFacilityId = user.getUser().id_correctional;
    const facilities: Facility[] = user.getFacilities();
    const defaultFacility = facilities.find(f => f.id === defaultFacilityId);
    setCurrentFacility(defaultFacility ?? null);
    setUserFacilities(facilities);
  }, [user]);

  return (
    <div className="patient-dashboard">
      <Box className="dashboard-filter" sx={{ width: "100%", mb: 2 }}>
        <Grid container alignItems="center" justifyContent="flex-start" spacing={1}>
          <Grid item xs={12}>
            <h1 className="text-3xl">Facility Dashboard</h1>
          </Grid>
          <Grid item xs={12} sx={{ ml: 2 }}>
            <Grid container spacing={1} alignItems="center">
              <Grid item xs={12} sm={4}>
                <Typography variant="body1">
                  <strong>Officer Name:</strong> {getFullName()}
                </Typography>
              </Grid>
              <Grid item xs={12} sm={8}>
                <Box display="flex" alignItems="center">
                  <Typography variant="body1">
                    <strong>Correctional Facility:&nbsp;&nbsp;</strong>
                  </Typography>
                  {userFacilities && currentFacility && (
                    <>
                      {userFacilities.length > 1 ? (
                        <FormControl fullWidth size="small" sx={{ maxWidth: "50%" }}>
                          <Select
                            value={currentFacility.id.toString()}
                            onChange={handleFacilityChange}
                          >
                            {userFacilities?.map((facility: Facility) => (
                              <MenuItem key={facility.id} value={facility.id}>
                                <Box
                                  component="span"
                                  title={facility.name}
                                  sx={{
                                    whiteSpace: 'nowrap',
                                    overflow: 'hidden',
                                    textOverflow: 'ellipsis',
                                    maxWidth: '100%',
                                  }}
                                >
                                  {facility.name}
                                </Box>
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      ) : (
                        <Typography variant="body1" sx={{ ml: 1 }}>
                          {currentFacility?.name}
                        </Typography>
                      )}
                    </>
                  )}
                </Box>
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </Box>
      <div className="flex relative">
        <div className="justify-between flex">
          <span>
            <IconButton
              aria-label="search"
              size="large"
              className="p-2"
              disabled
            >
              <SearchIcon fontSize="inherit" />
            </IconButton>
          </span>
          <SearchForm
            onSearch={handleSearchChangeDebounced}
            onStartDateChange={handleStartDateChange}
            onEndDateChange={handleEndDateChange}
          />
        </div>
        <Box className="justify-end absolute right-0 sm:right-full sm:left-8 sm:top-12 sm:w-full xs:right-full xs:left-7 xs:w-36 xs:top-36 xs:pt-2">
          <Button
            variant="contained"
            style={{
              backgroundColor: "#6d87ff",
            }}
            onClick={handleClick}
          >
            <AddIcon /> New Appointment
          </Button>
        </Box>
      </div>
      {isLoading && <FullPageLoader />}
      <>
        <div className="mt-5 overflow-auto">
          <PatientTable
            data={paginatedData ?? []}
            setRefresh={setRefresh}
            setIsLoading={setIsLoading}
          />
        </div>
        {paginatedData?.length > 0 && data && (
          <PaginationControl
            totalRecords={data?.length}
            recordsPerPage={recordsPerPage}
            currentPage={currentPage}
            onPageChange={handlePageChange}
          />
        )}
      </>
    </div>
  );
}

export default FacilityDashboard;